import React, { useState, useEffect } from 'react';
import { updateDoctor } from '../../logic/settings';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, FormControl, Row, Col, Container } from 'react-bootstrap';
import { clearStorage } from './../../libs/general';

function DoctorAlerts() {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams();
	const [doc, setDoc] = useState(null);
	const [error, setError] = useState('');
	
	useEffect(() => {
		if (location && location.state && location.state.doc) {
			const { doc } = location.state;
			// Ensure docId is present - use URL param as fallback if missing
			const docWithId = { ...doc };
			if (!docWithId.docId && params.docId) {
				docWithId.docId = params.docId;
			}
			
			// If no alerts exist, add an empty one
			if (!(docWithId.preferences && docWithId.preferences.alerts
				&& docWithId.preferences.alerts instanceof Array
				&& docWithId.preferences.alerts.length >= 1)) {
				if (!docWithId.preferences) docWithId.preferences = {};
				if (!docWithId.preferences.alerts) docWithId.preferences.alerts = [];
				docWithId.preferences.alerts.push({
					type: '',
					medium: '',
					daysPrior: '',
					hour: '',
					minute: ''
				});
			}
			
			setDoc(docWithId);
		}
	}, [location, params.docId]);
	
	const handleChange = (e, i) => {
		if (e && e.target && e.target.id && e.target.value) {
			const field = e.target.id;
			const value = e.target.value;

			const updatedDoc = {...doc};
			updatedDoc.preferences.alerts[i][field] = value;
			setDoc(updatedDoc);
		} else return;
	};
	
	const handleSave = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		// Ensure docId is present - use URL param as fallback if missing from doc object
		const docToSave = { ...doc };
		if (!docToSave.docId && params.docId) {
			docToSave.docId = params.docId;
		}

		const savedDoc = await updateDoctor(docToSave);
		clearStorage();

		if (savedDoc instanceof Error)
			handleError(savedDoc);
		else
			navigate("/settings/doctors");
	};
	
	const handleError = (e) => {
		console.log(e);
		setError(e);
	};
	
	const addAlert = () => {
		const updatedDoc = {...doc};

		if (!updatedDoc.preferences) updatedDoc.preferences = {};
		if (!updatedDoc.preferences.alerts) updatedDoc.preferences.alerts = [];

		updatedDoc.preferences.alerts.push({
			type: '',
			medium: '',
			daysPrior: '',
			hour: '',
			minute: ''
		});
		setDoc(updatedDoc);
	};
	
	const deleteAlert = (e, index) => {
		if (window.confirm('Are you sure you want to delete this alert?')) {
			e.preventDefault();
			e.stopPropagation();

			const updatedDoc = {...doc};

			if (!(updatedDoc.preferences || updatedDoc.preferences.alerts
				|| updatedDoc.preferences.alerts instanceof Array
				|| updatedDoc.preferences.alerts[index]))
				return;

			updatedDoc.preferences.alerts.splice(index, 1);
			setDoc(updatedDoc);
		}
	};
	
	// Only render if doc is loaded
	if (!doc) return <div>Loading...</div>;
	
	return (
		<div>
			<div className="pages__back-button--container">
				<FontAwesomeIcon className="pages__back-button--icon" icon="chevron-left"></FontAwesomeIcon>
				<Link className="pages__back-button--text" to="/settings/doctors">Back</Link>
			</div>
			<header className="pages__header--container centered-text">
				<h1>Doctor's Alerts</h1>
			</header>
			<Container>
				<form autoComplete='on' onSubmit={handleSave} style={{ paddingBottom: '60px' }}>
					{
						(doc.preferences && doc.preferences.alerts
							&& doc.preferences.alerts instanceof Array
							&& doc.preferences.alerts.length >= 1) ?
							doc.preferences.alerts.map((alert, i) => {
								return (
									<fieldset className="pages__form--fieldset" key={i}>
										<Row>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 3, offset: 3 }} lg={{ span: 3, offset: 3 }}>
												<Form.Group className="mb-3">
													<Form.Label>Type:</Form.Label>
													<Form.Control
														as="select"
														id='type'
														required
														value={alert.type}
														onChange={(e) => handleChange(e, i)}>
														<option value=''>--</option>
														<option value='fullsummary'>Full Summary</option>
														<option value='changesonly'>Changes Only</option>
														<option value='disabled'>Disabled</option>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Medium:</Form.Label>
													<Form.Control
														as="select"
														id='medium'
														required
														value={alert.medium}
														onChange={(e) => handleChange(e, i)}>
														<option value=''>--</option>
														<option value='emailonly'>Email</option>
														<option value='smsonly'>SMS</option>
														<option value='emailandsms'>Email & SMS</option>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Days prior to appointment&nbsp;<small>(0 - 60 days)</small></Form.Label>
													<Form.Control
														id='daysPrior'
														defaultValue={alert.daysPrior}
														onChange={(e) => handleChange(e, i)}
														required
														type='number'
														min="0"
														max="60">
													</Form.Control>
												</Form.Group>
											</Col>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 3, offset: 0 }} lg={{ span: 3, offset: 0 }}>
												<Form.Group className="mb-3">
													<Form.Label>Hour&nbsp;<small>(0 - 23)</small></Form.Label>
													<Form.Control
														id='hour'
														defaultValue={alert.hour}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min="0"
														max="23">
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Minute&nbsp;<small>(0 - 59)</small></Form.Label>
													<Form.Control
														id='minute'
														defaultValue={alert.minute}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min="0"
														max="59">
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }}
												className={
													doc.preferences.alerts.length - 1 === i ?
														"pages__form--col settings__list--actionIcons right-text no-border"
														: "pages__form--col settings__list--actionIcons right-text"
												}>
												<FontAwesomeIcon className="settings__list--delete " onClick={(e) => deleteAlert(e, i)} icon="trash-alt"></FontAwesomeIcon>
											</Col>
										</Row>
									</fieldset>
								)
							})
							: null
					}
					<div className="pages__action-bar--container">
						<div onClick={addAlert} className="pages__action-bar--item">
							<FontAwesomeIcon icon="plus" />
						</div>
					</div>
					<div className="pages__form--error centered-text">
						{
							error && error instanceof Array ?
								error.map((err, i) => <p key={i}>{err.toString()}</p>)
								: <p>{error.message}</p>
						}
					</div>
					<Row>
						<Col>
							<input className="save-button center-block small-button" type='submit' value='Save' />
						</Col>
					</Row>
				</form>
			</Container>
		</div>
	);
}

export default DoctorAlerts;